import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select
} from 'antd'
import { ModalProps } from 'antd/lib'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import {
  useClassifierServiceGetDeviceTypeClassifierElementListEndpoint,
  useInstitutionServiceCreateDeviceEndpoint,
  useInstitutionServiceGetDeviceEndpoint,
  useInstitutionServiceGetDeviceListEndpointKey,
  useInstitutionServiceUpdateDeviceEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  CreateDeviceTypeClassifierElementResponse
} from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { EditableFormItem, message, SaveConfirm } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

type Props = {
  onCancel: () => void
  id?: string
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId
      ? t('equipments.edit_equipment')
      : t('equipments.add_equipment'),
    button: hasId ? t('common.edit') : t('common.save')
  }
}

export const EquipmentModal = ({ id, onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()

  const classifierId = useClassifierId(
    ClassifierType.DEVICE_TYPE,
    ClassifierCategory.INSTITUTION
  )

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { data: devices, isLoading } =
    useClassifierServiceGetDeviceTypeClassifierElementListEndpoint({
      classifierId: String(classifierId),
      pageNumber: 1,
      pageSize: 100
    })

  const { data } = useInstitutionServiceGetDeviceEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const { mutate: create, isPending: isCreating } =
    useInstitutionServiceCreateDeviceEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useInstitutionServiceGetDeviceListEndpointKey]
        })
        message.success(t('equipments.create_success_equipment'))
        onCancel()
      }
    })

  const { mutate: edit, isPending: isEditing } =
    useInstitutionServiceUpdateDeviceEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useInstitutionServiceGetDeviceListEndpointKey]
        })
        message.success(t('equipments.edit_success_equipment'))
        onCancel()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const payload = {
      ...values,
      lastInspectionDate: toDayjs(values.lastInspectionDate)?.format()
    }

    return id
      ? edit({ id, requestBody: payload })
      : create({ requestBody: payload })
  }

  const deviceOptions = devices?.data?.map(
    (item: CreateDeviceTypeClassifierElementResponse) => ({
      value: item.id,
      label: item.translation?.[currentLang.value]
    })
  )

  const { title, button } = getModalText(!!id, t)

  useEffect(() => {
    if (data && id)
      form.setFieldsValue({
        ...data,
        lastInspectionDate: toDayjs(data.lastInspectionDate),
        deviceTypeId: data.type?.id
      })
  }, [data, id])

  return (
    <Modal
      centered
      width={800}
      maskClosable={false}
      open
      footer={null}
      title={title}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Form.Item
              name="code"
              label={t('equipments.code')}
              rules={requiredRules}
            >
              <Input placeholder={t('equipments.code')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="deviceTypeId"
              rules={requiredRules}
              label={t('equipments.device_type')}
            >
              <Select
                loading={isLoading}
                options={deviceOptions}
                placeholder={t('equipments.device_type')}
              />
            </Form.Item>
          </Col>

          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['name', langKey]}
                  label={`${t('medicine_register.products.name')} ${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={24}>
            <Form.Item
              name="lastInspectionDate"
              label={t('equipments.last_inspection_date')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                format={dayMonthYearFormatWithDot}
                disabledDate={disabledFutureDate}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="description" label={t('common.description')}>
              <Input.TextArea placeholder={t('common.description')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={16}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${button}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isCreating || isEditing }}
              >
                <Button type="primary">{button}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
